/**
 * lazyScreen — a module screen that downloads the first time it is opened.
 *
 * ADR-040: "each module's components load on first use, not at app boot".
 * A module's `createRoutes` keeps its route table eager (paths, params,
 * `validateSearch`, redirects — everything route matching needs) and takes
 * each screen through this helper:
 *
 *     const CustomerListPage = lazyScreen(
 *       () => import("./routes/customer-list"),
 *       "CustomerListPage",
 *     );
 *     createRoute({ ..., component: CustomerListPage });
 *
 * so the screen's code sits in its own chunk instead of every visitor's
 * first download (BND-01).
 *
 * Why not TanStack Router's `lazyRouteComponent`: the behaviour is the same on
 * purpose (a `.preload()` the router awaits before it renders the match, and
 * one reload when a chunk has vanished under a redeploy), but module route
 * tests stub `@tanstack/react-router` with hand-written mocks, and a helper
 * imported from that package would be missing inside them. This one needs
 * React only.
 *
 * What the user sees:
 *   - normal navigation: nothing new. The router calls `preload()` while it
 *     resolves the match and renders once the chunk is in; a slow chunk shows
 *     the route's `pendingComponent` (the cockpit sets one on every module
 *     route in `composeModuleRoutes`).
 *   - a chunk that no longer exists (a tab left open across a deploy that
 *     renamed the chunks): the page reloads ONCE to pick up the new build.
 *     The attempt is remembered in sessionStorage per failed URL, so a chunk
 *     that is genuinely broken cannot cause a reload loop.
 *   - any other load failure, or the same one after the reload: the error is
 *     thrown at render, so the module's error boundary shows "Couldn't load
 *     this module" — never a blank page.
 */

import { createElement, type ComponentType, type ReactElement } from "react";

/**
 * A lazily loaded screen: render it like a component, or preload it ahead.
 * `P` is the loaded component's own props (POP-01: a record pop-up body takes
 * the record's `id`); a route screen takes none.
 */
export interface LazyScreen<P extends object = object> {
  (props: P): ReactElement;
  /** Starts (or joins) the download. Never rejects — a failure surfaces at render. */
  preload: () => Promise<void>;
  displayName?: string;
}

/** The export names of `M` whose value is a React component. */
export type ScreenExport<M> = {
  [K in keyof M]: M[K] extends ComponentType<never> ? K : never;
}[keyof M] &
  string;

/**
 * True when an error means "the chunk file could not be fetched" — the
 * signature of a stale tab after a deploy. Messages per engine:
 *   Chromium: "Failed to fetch dynamically imported module: <url>"
 *   Firefox:  "error loading dynamically imported module: <url>"
 *   Safari:   "Importing a module script failed."
 *   Vite:     "Unable to preload CSS for <url>" (a chunk's stylesheet)
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = (error as { message?: unknown } | null | undefined)?.message;
  if (typeof message !== "string") return false;
  return (
    message.startsWith("Failed to fetch dynamically imported module") ||
    message.startsWith("error loading dynamically imported module") ||
    message.startsWith("Importing a module script failed") ||
    message.startsWith("Unable to preload CSS")
  );
}

/** sessionStorage key prefix recording that a failed chunk already reloaded once. */
export const CHUNK_RELOAD_KEY_PREFIX = "ken:chunk-reload:";

/** Claims the one reload allowed for this failure; false if it was used already. */
function claimReload(error: unknown): boolean {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined")
    return false;
  const key =
    CHUNK_RELOAD_KEY_PREFIX + String((error as { message?: unknown }).message);
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, "1");
    return true;
  } catch {
    // Storage refused (private mode, quota): no loop protection, so no reload.
    return false;
  }
}

/** One promise that never settles: suspends while a reload replaces the page. */
let forever: Promise<never> | undefined;

/** The props of export `K` of module `M`. */
type ScreenProps<M, K extends keyof M> =
  M[K] extends ComponentType<infer P> ? (P extends object ? P : object) : object;

export function lazyScreen<M extends object, K extends ScreenExport<M> = ScreenExport<M>>(
  load: () => Promise<M>,
  exportName: K,
): LazyScreen<ScreenProps<M, K>> {
  let screen: ComponentType<object> | undefined;
  let failure: unknown;
  let failed = false;
  let reloading = false;
  let reloadRequested = false;
  let pending: Promise<void> | undefined;

  const preload = (): Promise<void> => {
    if (screen || failed) return Promise.resolve();
    pending ??= load().then(
      (mod) => {
        const found = (mod as Record<string, unknown>)[exportName];
        if (
          typeof found === "function" ||
          (typeof found === "object" && found !== null)
        ) {
          screen = found as ComponentType<object>;
        } else {
          failed = true;
          failure = new Error(
            `Screen "${exportName}" is not exported by its chunk`,
          );
        }
        pending = undefined;
      },
      (error: unknown) => {
        failed = true;
        failure = error;
        reloading = isChunkLoadError(error) && claimReload(error);
        pending = undefined;
      },
    );
    return pending;
  };

  const Screen = ((props: object): ReactElement => {
    if (reloading) {
      // Asked for once: React may render this component again (StrictMode,
      // a Suspense retry) before the reload replaces the page.
      if (!reloadRequested) {
        reloadRequested = true;
        window.location.reload();
      }
      throw (forever ??= new Promise<never>(() => undefined));
    }
    if (failed) throw failure;
    // Suspend on the download. A thrown promise rather than React's `use()`:
    // both work in the browser, but `use()` on a promise created outside
    // render never re-renders under React's test `act()` (TanStack's own
    // lazyRouteComponent shows the same), so the screen could not be tested.
    if (!screen) throw preload();
    return createElement(screen as ComponentType<object>, props);
  }) as unknown as LazyScreen<ScreenProps<M, K>>;

  Screen.preload = preload;
  Screen.displayName = `Lazy(${exportName})`;
  return Screen;
}
